type AssetType = 'image' | 'video' | 'sound' | 'json';

const TOKEN_KEY = 'media-cdn-user-token';

const app = document.querySelector<HTMLDivElement>('#app');
if (!app) {
    throw new Error('App root not found');
}
const appRoot = app;

let token = sessionStorage.getItem(TOKEN_KEY) || '';

function assetUrl(type: AssetType, nameSpace: string, assetName: string, option?: string) {
    const baseUrl = `${window.location.origin}/v1/${type}/${encodeURIComponent(nameSpace)}/${encodeURIComponent(assetName)}`;
    if (type === 'image') return `${baseUrl}?width=${encodeURIComponent(option || '300')}`;
    if (type === 'json' && option) return `${baseUrl}?assign=${encodeURIComponent(option)}`;
    return baseUrl;
}

function render() {
    appRoot.innerHTML = `
        <main class="page-shell">
            <header class="hero">
                <p class="eyebrow">Easy Site</p>
                <h1>Media CDN</h1>
                <p>Upload media to any namespace, then use it anywhere.</p>
            </header>
            ${token ? dashboardTemplate() : loginTemplate()}
            ${viewerTemplate()}
        </main>`;

    document.querySelector<HTMLFormElement>('#login-form')?.addEventListener('submit', login);
    document.querySelector<HTMLButtonElement>('#logout-button')?.addEventListener('click', logout);
    document.querySelector<HTMLFormElement>('#upload-form')?.addEventListener('submit', upload);
    document.querySelector<HTMLFormElement>('#viewer-form')?.addEventListener('submit', viewAsset);
    document.querySelector<HTMLSelectElement>('#viewer-type')?.addEventListener('change', updateViewerOption);
    document.querySelector<HTMLButtonElement>('#copy-link')?.addEventListener('click', copyLink);
}

function loginTemplate() {
    return `<section class="card login-card" aria-labelledby="login-heading">
        <h2 id="login-heading">Sign in to upload</h2>
        <p class="muted">Uploads require a Media CDN account. Asset previews stay public.</p>
        <form id="login-form">
            <label>Email address<input name="emailAddress" type="email" autocomplete="email" required></label>
            <label>Password<input name="password" type="password" autocomplete="current-password" required></label>
            <p id="login-message" class="message" role="alert"></p>
            <button type="submit">Sign in</button>
        </form>
    </section>`;
}

function dashboardTemplate() {
    return `<section class="card" aria-labelledby="upload-heading">
        <div class="section-heading"><div><h2 id="upload-heading">Upload an asset</h2><p class="muted">You can upload to any namespace.</p></div><button id="logout-button" class="button-secondary" type="button">Sign out</button></div>
        <form id="upload-form">
            <div class="form-grid">
                <label>Asset type<select name="type"><option value="image">Image</option><option value="video">Video</option><option value="sound">Sound</option><option value="json">JSON</option></select></label>
                <label>Namespace<input name="nameSpace" placeholder="my-space" required></label>
                <label>Asset name<input name="assetName" placeholder="image.png" required></label>
                <label>File<input name="file" type="file" required></label>
            </div>
            <p class="field-hint">Images: jpeg, jpg, png, bmp, gif, ico, svg, tiff, webp · Videos: mp4, webm, ogg · Sound: mp3 · JSON: json</p>
            <p id="upload-message" class="message" role="alert"></p>
            <button type="submit">Upload asset</button>
        </form>
        <div id="upload-result" class="result" hidden></div>
    </section>`;
}

function viewerTemplate() {
    return `<section class="card" aria-labelledby="viewer-heading">
        <h2 id="viewer-heading">View an asset</h2>
        <p class="muted">Enter a namespace and asset name to check what is available.</p>
        <form id="viewer-form">
            <div class="form-grid">
                <label>Asset type<select id="viewer-type" name="type"><option value="image">Image</option><option value="video">Video</option><option value="sound">Sound</option><option value="json">JSON</option></select></label>
                <label>Namespace<input name="nameSpace" placeholder="my-space" required></label>
                <label>Asset name<input name="assetName" placeholder="image.png" required></label>
                <label id="viewer-option"><span>Image width</span><input name="option" type="number" value="300" min="1" max="5000"></label>
            </div>
            <button type="submit">View asset</button>
        </form>
        <div id="viewer-result" class="result" aria-live="polite"></div>
    </section>`;
}

async function login(event: SubmitEvent) {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const message = document.querySelector<HTMLElement>('#login-message');
    const data = new FormData(form);
    const response = await request('/v1/user/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailAddress: data.get('emailAddress'), password: data.get('password') }),
    });

    if (!response.ok || !response.data.success || !response.data.userToken) {
        setMessage(message, response.data.error || 'Unable to sign in.', true);
        return;
    }

    token = response.data.userToken;
    sessionStorage.setItem(TOKEN_KEY, token);
    render();
}

function logout() {
    token = '';
    sessionStorage.removeItem(TOKEN_KEY);
    render();
}

async function upload(event: SubmitEvent) {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const data = new FormData(form);
    const type = data.get('type') as AssetType;
    const nameSpace = `${data.get('nameSpace')}`.trim();
    const assetName = `${data.get('assetName')}`.trim();
    const file = (form.elements.namedItem('file') as HTMLInputElement).files?.[0];
    const message = document.querySelector<HTMLElement>('#upload-message');

    if (!file?.size) {
        setMessage(message, 'Choose a file to upload.', true);
        return;
    }

    const options: RequestInit = { method: 'POST', headers: { Authorization: `Bearer ${token}` } };
    if (type === 'json') {
        options.headers = { ...options.headers, 'Content-Type': 'application/json' };
        options.body = JSON.stringify({ data: await file.text() });
    } else {
        const uploadData = new FormData();
        uploadData.append('file', file);
        options.body = uploadData;
    }

    const response = await request(`/v1/${type}/${encodeURIComponent(nameSpace)}/${encodeURIComponent(assetName)}`, options);
    if (!response.ok || !response.data.success) {
        setMessage(message, response.data.error || 'Upload failed.', true);
        return;
    }

    setMessage(message, 'Upload complete. Your asset is ready to use.', false);
    showAsset('upload-result', type, nameSpace, assetName, type === 'image' ? '300' : undefined);
}

async function viewAsset(event: SubmitEvent) {
    event.preventDefault();
    const data = new FormData(event.currentTarget as HTMLFormElement);
    showAsset('viewer-result', data.get('type') as AssetType, `${data.get('nameSpace')}`.trim(), `${data.get('assetName')}`.trim(), `${data.get('option') || ''}`);
}

function updateViewerOption(event: Event) {
    const type = (event.currentTarget as HTMLSelectElement).value as AssetType;
    const option = document.querySelector<HTMLElement>('#viewer-option');
    const label = option?.querySelector('span');
    const input = option?.querySelector<HTMLInputElement>('input');
    if (!option || !label || !input) return;

    const hasOption = type === 'image' || type === 'json';
    option.hidden = !hasOption;
    input.disabled = !hasOption;
    label.textContent = type === 'json' ? 'JavaScript variable name' : 'Image width';
    input.type = type === 'image' ? 'number' : 'text';
    input.value = type === 'image' ? '300' : '';
    input.placeholder = type === 'json' ? 'const DATA' : '';
}

async function showAsset(targetId: string, type: AssetType, nameSpace: string, assetName: string, option?: string) {
    const target = document.querySelector<HTMLElement>(`#${targetId}`);
    if (!target) return;
    const url = assetUrl(type, nameSpace, assetName, option);
    target.hidden = false;
    target.innerHTML = `<div class="link-box"><label>Copy-paste link<input id="asset-link" readonly value="${url}"></label>${targetId === 'upload-result' ? '<button id="copy-link" class="button-secondary" type="button">Copy link</button>' : ''}</div><div class="preview" id="asset-preview">Loading preview…</div>`;
    target.querySelector<HTMLButtonElement>('#copy-link')?.addEventListener('click', copyLink);
    if (type === 'json') {
        await showJson(target, url);
        return;
    }
    const element = document.createElement(type === 'image' ? 'img' : type === 'video' ? 'video' : 'audio');
    element.src = url;
    if (element instanceof HTMLMediaElement) element.controls = true;
    if (element instanceof HTMLImageElement) element.alt = `${assetName} preview`;
    element.addEventListener('error', () => showNotFound(target, assetName));
    const preview = target.querySelector<HTMLElement>('#asset-preview');
    preview?.replaceChildren(element);
}

async function showJson(target: HTMLElement, url: string) {
    const response = await fetch(url);
    if (!response.ok) {
        showNotFound(target, 'JSON asset');
        return;
    }
    const preview = target.querySelector<HTMLElement>('#asset-preview');
    preview?.replaceChildren(Object.assign(document.createElement('pre'), { textContent: await response.text() }));
}

function showNotFound(target: HTMLElement, assetName: string) {
    const preview = target.querySelector<HTMLElement>('#asset-preview');
    if (!preview) return;
    const warning = document.createElement('p');
    warning.className = 'warning';
    warning.textContent = `We couldn't find “${assetName}”. Check the namespace, file name, and asset type.`;
    preview.replaceChildren(warning);
}

async function copyLink() {
    const input = document.querySelector<HTMLInputElement>('#asset-link');
    if (!input) return;
    await navigator.clipboard?.writeText(input.value);
}

async function request(url: string, options: RequestInit): Promise<{ ok: boolean; data: any }> {
    try {
        const response = await fetch(url, options);
        return { ok: response.ok, data: await response.json() };
    } catch {
        return { ok: false, data: { error: 'Unable to reach the API.' } };
    }
}

function setMessage(element: HTMLElement | null, message: string, isError: boolean) {
    if (!element) return;
    element.textContent = message;
    element.classList.toggle('message-error', isError);
}

render();
